/**
 * Sessie 86: het blok "Dossier" in de copiloot, onder "Wat is gecontroleerd".
 * Inklapbaar en standaard dicht. Inhoud uit het kladblok van de laatste
 * motorbeurt: samenvatting, wat onbekend is (eigen regel), de tijdlijn met de
 * dagen ertussen, de klantfeiten en de foto-narekening. Geen kladblok: niets.
 */
import { useState } from "react";

import type { V6Dossier } from "@/types/api";

import { Icoon } from "../Icoon";
import "./dossier.css";

const WAT: Record<string, string> = {
  besteld: "Besteld",
  verzonden: "Verzonden",
  bezorgd: "Bezorgd",
  gemeld: "Gemeld",
  tht: "THT",
  laatste_contact: "Laatste bericht klant",
};

const NAREKENING: Record<string, string> = {
  product_op_order: "product op order",
  batch_past_bij_verzendweek: "batch past bij verzendweek",
  tht_verlopen: "THT verlopen",
  dagen_bezorgd_tot_melding: "dagen bezorgd tot melding",
  eerder_gebruikt: "foto eerder gebruikt",
};

function datum(op: string): string {
  const d = new Date(op);
  if (Number.isNaN(d.getTime())) return op;
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short", timeZone: "Europe/Amsterdam" });
}

export function DossierBlok({ dossier }: { dossier: V6Dossier | null | undefined }) {
  const [open, setOpen] = useState(false);
  if (!dossier) return null;
  const onbekend = dossier.onbekend.length;
  return (
    <div className={`check dossier${open ? "" : " dicht"}`} data-testid="dossier-blok">
      <button
        type="button"
        className="t"
        aria-expanded={open}
        aria-controls="v6-dossier"
        onClick={() => setOpen(!open)}
      >
        <span className="wat">Dossier</span>
        <span className="titel">
          {onbekend ? `${onbekend} onbekend` : "niets onbekend"}
          <span className="pijl">
            <Icoon naam="pijl" klein />
          </span>
        </span>
      </button>
      <div className="bi" id="v6-dossier">
        {open ? (
          <>
            {dossier.samenvatting ? <p className="samen">{dossier.samenvatting}</p> : null}
            {onbekend ? (
              <>
                <p className="kop">Onbekend</p>
                <ul className="onbekend" data-testid="dossier-onbekend">
                  {dossier.onbekend.map((o) => (
                    <li key={o}>{o}</li>
                  ))}
                </ul>
              </>
            ) : null}
            {dossier.tijdlijn.length ? (
              <>
                <p className="kop">Tijdlijn</p>
                <ul data-testid="dossier-tijdlijn">
                  {dossier.tijdlijn.map((p, i) => (
                    <li key={`${p.wat}-${i}`}>
                      {datum(p.op)} {WAT[p.wat] ?? p.wat}
                      {p.dagen_na_vorige !== null && i > 0 ? (
                        <span className="tussen">{` (+${p.dagen_na_vorige} d)`}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {dossier.klantfeiten.length ? (
              <>
                <p className="kop">Klantfeiten</p>
                <ul>
                  {dossier.klantfeiten.map((f, i) => (
                    <li key={`f-${i}`}>{f}</li>
                  ))}
                </ul>
              </>
            ) : null}
            {dossier.fotos.length ? (
              <>
                <p className="kop">Foto's nagerekend</p>
                <ul>
                  {dossier.fotos.map((f, i) => (
                    <li key={`foto-${i}`}>
                      {f.product}
                      {Object.entries(f.narekening).length ? ": " : ""}
                      {Object.entries(f.narekening)
                        .map(([k, v]) => `${NAREKENING[k] ?? k.replace(/_/g, " ")} ${v}`)
                        .join(", ")}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
